/**
 * The fixed catalog of 172 exercises across 12 muscle groups (D-15). 158 until D-71,
 * 161 until D-83, 162 until D-93, 165 until D-94, 166 until D-102, 167 until D-116, 170 until D-117.
 *
 * Transcribed unchanged from §8 of the superseded iOS spec
 * (`docs/specs/2026-09-02-altrtrack-design.md`), which stays the authoritative source
 * for names, bodyweight factors, and duration tags. Do not edit a factor here without
 * editing it there — and read C-11 first: `score` is denormalised onto every workout
 * document, so changing a factor needs a one-off rescore pass to take effect on history.
 *
 * Conventions carried over from the web tracker:
 *   - Machine and free-weight movements where you do not lift yourself are 0.
 *   - Squat patterns use ~0.15 — the bar load plus a modest share of bodyweight.
 *   - Assisted variants take half the unassisted factor, and therefore must not also be
 *     flagged `supported` (D-21).
 *   - Machine weight is not counted; record added plates only.
 *
 * Ids are hand-assigned and permanent (D-31). They are stored on every logged exercise,
 * so renaming one silently repoints history at a different movement.
 */
function group(muscleGroup, rows) {
    return rows.map(([id, name, bodyweightFactor, dur]) => ({
        id,
        name,
        muscleGroup,
        bodyweightFactor,
        measure: dur === "dur" ? "duration" : "reps",
    }));
}
const CHEST = [
    ["flat-barbell-bench-press", "Flat Barbell Bench Press", 0],
    ["incline-barbell-bench-press", "Incline Barbell Bench Press", 0],
    ["decline-barbell-bench-press", "Decline Barbell Bench Press", 0],
    ["flat-dumbbell-press", "Flat Dumbbell Press", 0],
    ["incline-dumbbell-press", "Incline Dumbbell Press", 0],
    ["decline-dumbbell-press", "Decline Dumbbell Press", 0],
    ["machine-chest-press", "Machine Chest Press", 0],
    ["smith-machine-bench-press", "Smith Machine Bench Press", 0],
    // Added 2026-09-14 (D-93): the incline and decline benches on the Smith machine.
    ["incline-smith-machine-bench-press", "Incline Smith Machine Bench Press", 0],
    ["decline-smith-machine-bench-press", "Decline Smith Machine Bench Press", 0],
    ["flat-dumbbell-fly", "Flat Dumbbell Fly", 0],
    ["incline-dumbbell-fly", "Incline Dumbbell Fly", 0],
    ["high-to-low-cable-fly", "High-to-Low Cable Fly", 0],
    ["mid-cable-fly", "Mid Cable Fly", 0],
    ["low-to-high-cable-fly", "Low-to-High Cable Fly", 0],
    // Added 2026-09-22 (D-102): lying on an incline bench between two low pulleys. Not the
    // standing Low-to-High Cable Fly, which works the same fibres but is a different movement.
    ["incline-cable-fly", "Incline Cable Fly", 0],
    ["pec-deck", "Pec Deck", 0],
    ["push-up", "Push-Up", 0.65],
    // Added 2026-09-14 (D-94). From the knees the body pivots there instead of at the feet,
    // so less of it is lifted and the lever is shorter: below Push-Up's 0.65.
    ["knee-push-up", "Knee Push-Up", 0.45],
    ["weighted-push-up", "Weighted Push-Up", 0.65],
    ["decline-push-up", "Decline Push-Up", 0.75],
    ["chest-dips", "Chest Dips", 1.0],
];
const BACK = [
    ["conventional-deadlift", "Conventional Deadlift", 0],
    ["sumo-deadlift", "Sumo Deadlift", 0],
    ["barbell-row", "Barbell Row", 0],
    // Added 2026-10-14 (D-117): hands inside shoulder width, elbows tucked. Its own entry,
    // like the pulldown and rope curl beside their neighbours (D-116).
    ["close-grip-bent-over-barbell-row", "Close-Grip Bent-Over Barbell Row", 0],
    ["pendlay-row", "Pendlay Row", 0],
    ["single-arm-dumbbell-row", "Single-Arm Dumbbell Row", 0],
    ["t-bar-row", "T-Bar Row", 0],
    ["chest-supported-dumbbell-row", "Chest-Supported Dumbbell Row", 0],
    ["seated-cable-row", "Seated Cable Row", 0],
    ["plate-loaded-machine-row", "Plate-Loaded Machine Row", 0],
    ["machine-high-row", "Machine High Row", 0],
    ["machine-low-row", "Machine Low Row", 0],
    ["pull-up", "Pull-Up", 1.0],
    ["chin-up", "Chin-Up", 1.0],
    ["weighted-pull-up", "Weighted Pull-Up", 1.0],
    ["assisted-pull-up", "Assisted Pull-Up", 0.5],
    ["wide-grip-lat-pulldown", "Wide-Grip Lat Pulldown", 0],
    // Added 2026-09-09 (D-83). A different movement on the same machine, and both were
    // logged in one real message — one entry would have merged two exercises into one row
    // of history with nothing to separate them afterwards.
    ["wide-grip-behind-neck-lat-pulldown", "Wide-Grip Behind-Neck Lat Pulldown", 0],
    ["close-grip-lat-pulldown", "Close-Grip Lat Pulldown", 0],
    // Added 2026-10-14 (D-116): the V handle, palms facing. Kept apart from Close-Grip, which
    // the user does with a straight close-grip bar, so the two keep separate histories.
    ["v-bar-lat-pulldown", "V-Bar Lat Pulldown", 0],
    ["neutral-grip-lat-pulldown", "Neutral-Grip Lat Pulldown", 0],
    ["single-arm-lat-pulldown", "Single-Arm Lat Pulldown", 0],
    ["straight-arm-pulldown", "Straight-Arm Pulldown", 0],
    ["dumbbell-pullover", "Dumbbell Pullover", 0],
    ["rack-pull", "Rack Pull", 0],
    ["face-pull", "Face Pull", 0],
];
const SHOULDERS = [
    ["standing-barbell-overhead-press", "Standing Barbell Overhead Press", 0],
    ["seated-barbell-overhead-press", "Seated Barbell Overhead Press", 0],
    ["seated-dumbbell-shoulder-press", "Seated Dumbbell Shoulder Press", 0],
    ["arnold-press", "Arnold Press", 0],
    ["machine-shoulder-press", "Machine Shoulder Press", 0],
    ["lateral-side-raise", "Lateral Side Raise", 0],
    ["cable-lateral-raise", "Cable Lateral Raise", 0],
    ["machine-lateral-raise", "Machine Lateral Raise", 0],
    ["dumbbell-front-raise", "Dumbbell Front Raise", 0],
    ["cable-front-raise", "Cable Front Raise", 0],
    ["plate-front-raise", "Plate Front Raise", 0],
    ["dumbbell-rear-delt-fly", "Dumbbell Rear Delt Fly", 0],
    ["cable-rear-delt-fly", "Cable Rear Delt Fly", 0],
    ["reverse-pec-deck", "Reverse Pec Deck", 0],
    ["upright-row", "Upright Row", 0],
    ["barbell-shrug", "Barbell Shrug", 0],
    ["dumbbell-shrug", "Dumbbell Shrug", 0],
    ["landmine-press", "Landmine Press", 0],
];
const BICEPS = [
    ["barbell-curl", "Barbell Curl", 0],
    ["ez-bar-curl", "EZ-Bar Curl", 0],
    ["standing-dumbbell-curl", "Standing Dumbbell Curl", 0],
    ["seated-dumbbell-curl", "Seated Dumbbell Curl", 0],
    ["alternating-dumbbell-curl", "Alternating Dumbbell Curl", 0],
    ["incline-dumbbell-curl", "Incline Dumbbell Curl", 0],
    ["hammer-curl", "Hammer Curl", 0],
    ["barbell-preacher-curl", "Barbell Preacher Curl", 0],
    ["dumbbell-preacher-curl", "Dumbbell Preacher Curl", 0],
    ["machine-preacher-curl", "Machine Preacher Curl", 0],
    ["concentration-curl", "Concentration Curl", 0],
    ["straight-bar-cable-curl", "Straight-Bar Cable Curl", 0],
    ["rope-cable-curl", "Rope Cable Curl", 0],
    // Added 2026-10-14 (D-116), at the user's choice a separate entry from Rope Cable Curl
    // rather than a rename: history already logged there stays there.
    ["rope-hammer-curl", "Rope Hammer Curl", 0],
    ["single-arm-cable-curl", "Single-Arm Cable Curl", 0],
    ["spider-curl", "Spider Curl", 0],
    ["machine-bicep-curl", "Machine Bicep Curl", 0],
];
const TRICEPS = [
    ["close-grip-bench-press", "Close-Grip Bench Press", 0],
    ["ez-bar-skull-crusher", "EZ-Bar Skull Crusher", 0],
    ["dumbbell-skull-crusher", "Dumbbell Skull Crusher", 0],
    ["dumbbell-overhead-tricep-extension", "Dumbbell Overhead Tricep Extension", 0],
    ["cable-rope-overhead-tricep-extension", "Cable Rope Overhead Tricep Extension", 0],
    ["rope-tricep-pushdown", "Rope Tricep Pushdown", 0],
    ["straight-bar-tricep-pushdown", "Straight-Bar Tricep Pushdown", 0],
    ["v-bar-tricep-pushdown", "V-Bar Tricep Pushdown", 0],
    // Added 2026-10-14 (D-117): the straight bar held palms up. What the user calls a
    // "reverse tricep extension" — the alias in matcher.ts.
    ["reverse-grip-tricep-pushdown", "Reverse-Grip Tricep Pushdown", 0],
    ["tricep-dips", "Tricep Dips", 1.0],
    ["tricep-kickback", "Tricep Kickback", 0],
    ["machine-tricep-extension", "Machine Tricep Extension", 0],
    ["bench-dips", "Bench Dips", 0.45],
    ["diamond-push-up", "Diamond Push-Up", 0.7],
];
const FOREARMS = [
    ["barbell-wrist-curl", "Barbell Wrist Curl", 0],
    ["dumbbell-wrist-curl", "Dumbbell Wrist Curl", 0],
    ["reverse-wrist-curl", "Reverse Wrist Curl", 0],
    ["barbell-reverse-curl", "Barbell Reverse Curl", 0],
    ["ez-bar-reverse-curl", "EZ-Bar Reverse Curl", 0],
    ["farmers-carry", "Farmer's Carry", 0, "dur"],
    ["plate-pinch", "Plate Pinch", 0, "dur"],
    ["dead-hang", "Dead Hang", 0.12, "dur"],
];
const QUADS = [
    ["back-squat", "Back Squat", 0.15],
    ["front-squat", "Front Squat", 0.15],
    ["goblet-squat", "Goblet Squat", 0.15],
    ["hack-squat", "Hack Squat", 0.1],
    ["leg-press", "Leg Press", 0],
    ["smith-machine-squat", "Smith Machine Squat", 0.15],
    ["bulgarian-split-squat", "Bulgarian Split Squat", 0.4],
    ["walking-lunge", "Walking Lunge", 0.4],
    ["reverse-lunge", "Reverse Lunge", 0.4],
    ["step-up", "Step-Up", 0.45],
    ["leg-extension", "Leg Extension", 0],
    ["sissy-squat", "Sissy Squat", 0.25],
    ["pendulum-squat", "Pendulum Squat", 0.1],
];
const HAMSTRINGS = [
    ["barbell-romanian-deadlift", "Barbell Romanian Deadlift", 0],
    ["dumbbell-romanian-deadlift", "Dumbbell Romanian Deadlift", 0],
    ["stiff-leg-deadlift", "Stiff-Leg Deadlift", 0],
    ["lying-leg-curl", "Lying Leg Curl", 0],
    ["seated-leg-curl", "Seated Leg Curl", 0],
    ["nordic-hamstring-curl", "Nordic Hamstring Curl", 0.6],
    ["good-morning", "Good Morning", 0.1],
    ["glute-ham-raise", "Glute-Ham Raise", 0.55],
];
const GLUTES = [
    ["barbell-hip-thrust", "Barbell Hip Thrust", 0],
    ["machine-hip-thrust", "Machine Hip Thrust", 0],
    ["glute-bridge", "Glute Bridge", 0.3],
    ["cable-kickback", "Cable Kickback", 0],
    ["cable-pull-through", "Cable Pull-Through", 0],
    ["hip-abduction-machine", "Hip Abduction Machine", 0],
    ["hip-adduction-machine", "Hip Adduction Machine", 0],
    ["kettlebell-swing", "Kettlebell Swing", 0],
];
const CALVES = [
    ["standing-machine-calf-raise", "Standing Machine Calf Raise", 0.22],
    ["smith-machine-calf-raise", "Smith Machine Calf Raise", 0.22],
    ["dumbbell-standing-calf-raise", "Dumbbell Standing Calf Raise", 0.22],
    ["seated-calf-raise", "Seated Calf Raise", 0],
    ["leg-press-calf-raise", "Leg Press Calf Raise", 0],
    ["donkey-calf-raise", "Donkey Calf Raise", 0.22],
];
const CORE = [
    ["plank", "Plank", 0.12, "dur"],
    ["weighted-plank", "Weighted Plank", 0.12, "dur"],
    ["side-plank", "Side Plank", 0.08, "dur"],
    ["hanging-leg-raise", "Hanging Leg Raise", 0.25],
    ["hanging-knee-raise", "Hanging Knee Raise", 0.35],
    // Added 2026-09-14 (D-93). On the floor only the legs move, through an arc whose lever
    // shortens as they rise — so below the hanging raise, level with Dead Bug.
    ["lying-leg-raise", "Lying Leg Raise", 0.15],
    ["cable-crunch", "Cable Crunch", 0],
    ["machine-crunch", "Machine Crunch", 0],
    // Added 2026-10-14 (D-116). On the floor, head and shoulders only — a short lever over a
    // short range, so Dead Bug's tier rather than the sit-up's 0.35.
    ["half-crunch", "Half Crunch", 0.15],
    ["ab-wheel-rollout", "Ab Wheel Rollout", 0.45],
    ["decline-sit-up", "Decline Sit-Up", 0.35],
    ["russian-twist", "Russian Twist", 0.15],
    ["cable-woodchop", "Cable Woodchop", 0],
    ["pallof-press", "Pallof Press", 0],
    ["dead-bug", "Dead Bug", 0.15],
    ["mountain-climbers", "Mountain Climbers", 0.15, "dur"],
    ["back-extension-45", "45° Back Extension", 0.45],
    ["hyperextension", "Hyperextension", 0.45],
];
const FULL_BODY = [
    ["clean-and-press", "Clean & Press", 0],
    ["power-clean", "Power Clean", 0],
    ["thruster", "Thruster", 0.15],
    ["sled-push", "Sled Push", 0, "dur"],
    ["sled-pull", "Sled Pull", 0, "dur"],
    ["battle-ropes", "Battle Ropes", 0.15, "dur"],
    ["burpee", "Burpee", 0.7],
    ["box-jump", "Box Jump", 0.8],
    // 0.25 until 2026-09-06 (D-72), where 20 minutes scored 6,700 at 67kg — more than most
    // real training days. Level with the treadmill: harder per minute than running, but
    // seated, and the two cancel. Battle Ropes above keeps 0.20 because it is done in
    // 30-60s bursts, so the time inflation never arises.
    ["rowing-machine", "Rowing Machine", 0.15, "dur"],
    ["assault-bike", "Assault Bike", 0.15, "dur"],
    ["ski-erg", "Ski Erg", 0.15, "dur"],
    // Appended 2026-09-05 for the gym_data.csv import (D-71). These sit BELOW the machine
    // tier above on purpose. Per-rep, a treadmill deserves more than a rower — it is the
    // only weight-bearing machine here — but the score multiplies load by time and cardio
    // is long: at 0.4 a 45-minute session outscored a 35-set lifting day by 66%. The three
    // above have the same problem and nothing has been logged against them yet.
    ["treadmill", "Treadmill", 0.15, "dur"],
    ["elliptical", "Elliptical", 0.1, "dur"],
    ["cycle", "Cycle", 0.08, "dur"],
];
/** All 172, in spec order: group by group, and within a group in the spec's own order. */
export const BUILT_IN_CATALOG = Object.freeze([
    ...group("chest", CHEST),
    ...group("back", BACK),
    ...group("shoulders", SHOULDERS),
    ...group("biceps", BICEPS),
    ...group("triceps", TRICEPS),
    ...group("forearms", FOREARMS),
    ...group("quads", QUADS),
    ...group("hamstrings", HAMSTRINGS),
    ...group("glutes", GLUTES),
    ...group("calves", CALVES),
    ...group("core", CORE),
    ...group("fullBody", FULL_BODY),
]);
/*
 * The catalog in use: the built-ins, plus whatever the admin has added or renamed from
 * the portal (B-66, D-118), installed by `installCatalog` in catalogEdits.ts.
 *
 * One array, mutated in place, rather than a rebound export: every importer holds this
 * same reference, so an install reaches the bot, the picker and the scorer without any
 * of them re-importing. Until something is installed it is exactly the built-ins, which
 * is what every test that never installs anything sees.
 */
const current = [...BUILT_IN_CATALOG];
export const CATALOG = current;
let byId = new Map(current.map((e) => [e.id, e]));
/** Extra phrases that find an exercise: the admin's "other names", and a renamed
 *  built-in's original name, so a message written the old way still lands. */
let extraAliases = [];
let revision = 0;
/**
 * Replace the catalog in use. Only catalogEdits.ts calls this — it is what decides what
 * may be installed; this only swaps the contents and bumps the revision the matcher
 * rebuilds its indexes on.
 */
export function replaceCatalog(entries, aliases) {
    current.splice(0, current.length, ...entries);
    byId = new Map(current.map((e) => [e.id, e]));
    extraAliases = aliases;
    revision += 1;
}
/** Bumped on every install, so a cache derived from the catalog knows when to rebuild. */
export function catalogRevision() {
    return revision;
}
/** The aliases installed with the current catalog. See `replaceCatalog`. */
export function installedAliases() {
    return extraAliases;
}
/** The catalog entry for `id`, or undefined. Callers holding a stored `catalogId` should
 *  treat undefined as "logged under an id this build does not know", never as an error. */
export function exerciseById(id) {
    return byId.get(id);
}
/** Every exercise in one group, in spec order. */
export function exercisesByGroup(muscleGroup) {
    return CATALOG.filter((e) => e.muscleGroup === muscleGroup);
}
//# sourceMappingURL=catalog.js.map